// The web app's typed client. No generation step: the types flow from the specs' Zod schemas.
//
//   const actions = createActionClient({ specs: allSpecs, headers: () => ({ "x-csrf": token }) });
//   const rows = actions.useActionQuery("lists.rows", { listId });
//   const setStatus = actions.useAction("lists.setRowStatus");
//   setStatus.mutate({ listId, rowId, status: "Called" });   // the row moves at once; rolls back if refused
//
// Data lives in TanStack Query's memory cache only. Never add a persister: nothing is written to browser
// storage (HANDOFF §6). The live channel calls `invalidateRefs` with what changed on the server.
//
// Isomorphic imports only (spec.ts, errors.ts): no server code reaches the browser.

import {
  type QueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { messageFor } from "../errors";
import type {
  ActionErrorCode,
  ActionResult,
  ActionSuccess,
  AnyActionSpec,
  ConfirmationRequest,
  InputOf,
  Locale,
  ObjectRef,
  OptimisticCache,
  OutputOf,
  RawInputOf,
  SpecMap,
} from "../spec";
import { ERROR_STATUS, refsIntersect } from "../status";

export const ACTION_QUERY_ROOT = "action";

/**
 * What the API itself answers before a call reaches the gate: not signed in, a CSRF check that failed (a
 * stale tab: reload), no access to this company, the request rate limit, the API unavailable.
 */
export type PlatformErrorCode =
  | "unauthenticated"
  | "no_access"
  | "company_required"
  | "csrf_failed"
  | "validation_failed"
  | "rate_limited"
  | "unavailable";

const PLATFORM_CODES: ReadonlySet<string> = new Set<PlatformErrorCode>([
  "unauthenticated",
  "no_access",
  "company_required",
  "csrf_failed",
  "validation_failed",
  "rate_limited",
  "unavailable",
]);

function platformCode(code: string): ActionErrorCode | PlatformErrorCode {
  if (PLATFORM_CODES.has(code)) return code as PlatformErrorCode;
  if (code in ERROR_STATUS) return code as ActionErrorCode;
  return "internal";
}

/** A refused or failed call. `code` is typed; `message` is written for the person. */
export class ActionClientError extends Error {
  readonly code: ActionErrorCode | PlatformErrorCode | "cancelled";
  readonly status: number;
  readonly details: unknown;

  constructor(
    code: ActionErrorCode | PlatformErrorCode | "cancelled",
    message: string,
    status: number,
    details?: unknown,
  ) {
    super(message);
    this.name = "ActionClientError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface ActionClientOptions<M extends SpecMap> {
  specs: M;
  /** Where the API is; default the same origin (`<company>.adea.app`). */
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Headers on every call (the CSRF header the API checks on writes). */
  headers?: () => Record<string, string>;
  locale?: () => Locale;
  /**
   * The server asked for the person's confirmation. Show the preview card; when they click Confirm, call
   * `client.confirm(...)` and resolve with its token, or resolve null when they cancel.
   */
  onConfirmationRequired?: (request: ConfirmationRequest, input: unknown) => Promise<string | null>;
}

type Name<M> = Extract<keyof M, string>;

export interface CallOptions {
  /** A token from `confirm`, when the person has already confirmed this exact call. */
  confirmation?: string;
  signal?: AbortSignal;
}

export function createActionClient<M extends SpecMap>(options: ActionClientOptions<M>) {
  const base = `${options.baseUrl ?? ""}/api/v1/actions`;
  const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const locale = () => options.locale?.() ?? "en";

  function specOf(name: string): AnyActionSpec {
    const spec = (options.specs as Record<string, AnyActionSpec>)[name];
    if (!spec) throw new Error(`Unknown action "${name}"`);
    return spec;
  }

  async function post(path: string, body: unknown, extra: Record<string, string>, signal?: AbortSignal) {
    let response: Response;
    try {
      response = await doFetch(`${base}/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", ...options.headers?.(), ...extra },
        body: JSON.stringify(body ?? {}),
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new ActionClientError("internal", messageFor("internal", locale()), 0);
    }
    const json = (await response.json().catch(() => null)) as unknown;
    if (!json || typeof json !== "object")
      throw new ActionClientError("internal", messageFor("internal", locale()), response.status);
    if (typeof (json as { ok?: unknown }).ok === "boolean") return json as ActionResult;
    // Not the gate's answer: the platform's own (sign-in, CSRF, the request rate limit, a company the person
    // isn't in, the API down). Its message is already in the reader's language; keep it and its status.
    const flat = json as { code?: unknown; message?: unknown; details?: unknown };
    const code = typeof flat.code === "string" ? flat.code : "internal";
    throw new ActionClientError(
      platformCode(code),
      typeof flat.message === "string" && flat.message ? flat.message : messageFor("internal", locale()),
      response.status,
      flat.details,
    );
  }

  function toError(result: Extract<ActionResult, { ok: false }>): ActionClientError {
    const { code, message, details } = result.error;
    return new ActionClientError(code, message, ERROR_STATUS[code] ?? 500, details);
  }

  /** Runs an action. Asks for a confirmation through `onConfirmationRequired` when the server wants one. */
  async function call<N extends Name<M>>(
    name: N,
    input: RawInputOf<M[N]>,
    callOptions: CallOptions = {},
  ): Promise<ActionSuccess<OutputOf<M[N]>>> {
    const headers: Record<string, string> = callOptions.confirmation
      ? { "x-adea-confirmation": callOptions.confirmation }
      : {};
    const result = await post(encodeURIComponent(name), input, headers, callOptions.signal);
    if (result.ok) return result as ActionSuccess<OutputOf<M[N]>>;
    if (
      result.error.code === "confirmation_required" &&
      !callOptions.confirmation &&
      options.onConfirmationRequired
    ) {
      const token = await options.onConfirmationRequired(result.error.details as ConfirmationRequest, input);
      if (!token) throw new ActionClientError("cancelled", "", 0);
      return call(name, input, { ...callOptions, confirmation: token });
    }
    throw toError(result);
  }

  /** What the confirmation card shows, before the person decides. */
  async function prepare<N extends Name<M>>(name: N, input: RawInputOf<M[N]>): Promise<ConfirmationRequest> {
    const result = await post(`${encodeURIComponent(name)}/prepare`, input, {});
    if (!result.ok) throw toError(result);
    return result.data as ConfirmationRequest;
  }

  /** The person clicked Confirm (and typed the name, for a strong confirmation). Returns the token. */
  async function confirm<N extends Name<M>>(
    name: N,
    input: RawInputOf<M[N]>,
    typed?: string,
  ): Promise<string> {
    const body = typed === undefined ? { input } : { input, typed };
    const result = await post(`${encodeURIComponent(name)}/confirm`, body, {});
    if (!result.ok) throw toError(result);
    return (result.data as { token: string }).token;
  }

  function queryKey<N extends Name<M>>(name: N, input?: RawInputOf<M[N]>): QueryKey {
    return input === undefined ? [ACTION_QUERY_ROOT, name] : [ACTION_QUERY_ROOT, name, input];
  }

  /** Query options for a read action; `meta.refs` lets a change elsewhere refetch it. */
  function queryOptions<N extends Name<M>>(name: N, input: RawInputOf<M[N]>) {
    const spec = specOf(name);
    if (spec.kind !== "read") throw new Error(`"${name}" changes things; use useAction for it`);
    const parsed = spec.input.safeParse(input);
    const refs: ObjectRef[] = parsed.success && spec.reads ? spec.reads(parsed.data) : [{ type: name }];
    return {
      queryKey: queryKey(name, input),
      queryFn: async ({ signal }: { signal: AbortSignal }) => (await call(name, input, { signal })).data,
      meta: { refs },
    } satisfies UseQueryOptions<OutputOf<M[N]>, ActionClientError>;
  }

  function useActionQuery<N extends Name<M>>(
    name: N,
    input: RawInputOf<M[N]>,
    extra: Omit<UseQueryOptions<OutputOf<M[N]>, ActionClientError>, "queryKey" | "queryFn"> = {},
  ) {
    return useQuery<OutputOf<M[N]>, ActionClientError>({ ...queryOptions(name, input), ...extra });
  }

  /**
   * A change action as a mutation. Its spec's `optimistic` patch is applied at once (only for actions that
   * run without a confirmation) and rolled back if the server refuses; afterwards everything it touched is
   * refetched.
   */
  function useAction<N extends Name<M>>(
    name: N,
    extra: Omit<
      UseMutationOptions<ActionSuccess<OutputOf<M[N]>>, ActionClientError, RawInputOf<M[N]>, Rollback>,
      "mutationFn" | "onMutate"
    > = {},
  ) {
    const queryClient = useQueryClient();
    const spec = specOf(name);
    return useMutation<ActionSuccess<OutputOf<M[N]>>, ActionClientError, RawInputOf<M[N]>, Rollback>({
      ...extra,
      mutationKey: [ACTION_QUERY_ROOT, name],
      mutationFn: (input) => call(name, input),
      onMutate: async (input) => applyOptimistic(queryClient, spec, input),
      onError: (error, input, rollback, context) => {
        rollback?.undo();
        return extra.onError?.(error, input, rollback, context);
      },
      onSuccess: (result, input, rollback, context) => {
        rollback?.keep();
        return extra.onSuccess?.(result, input, rollback, context);
      },
      onSettled: async (result, error, input, rollback, context) => {
        await invalidateRefs(queryClient, result?.changed ?? touchedBy(spec, input));
        return extra.onSettled?.(result, error, input, rollback, context);
      },
    });
  }

  return {
    specs: options.specs,
    call,
    prepare,
    confirm,
    queryKey,
    queryOptions,
    useActionQuery,
    useAction,
    invalidateRefs,
  };
}

export type ActionClient<M extends SpecMap> = ReturnType<typeof createActionClient<M>>;

/**
 * An instant update's way back. `undo` takes out this call's patches only: another change made meanwhile to
 * the same cached read keeps its own patch. `keep` makes them part of the read once the server agreed.
 */
type Rollback = { undo(): void; keep(): void } | undefined;

interface Patch {
  spec: AnyActionSpec;
  match: Record<string, unknown> | undefined;
  fn: (old: unknown) => unknown;
}

/** Per cached read: the value the patches started from, and the patches still waiting for the server. */
interface Pending {
  base: unknown;
  patches: Array<{ id: number; fn: (old: unknown) => unknown }>;
}

const pendingByClient = new WeakMap<QueryClient, Map<string, Pending>>();
let nextPatchId = 1;

function pendingOf(queryClient: QueryClient): Map<string, Pending> {
  let pending = pendingByClient.get(queryClient);
  if (!pending) {
    pending = new Map();
    pendingByClient.set(queryClient, pending);
  }
  return pending;
}

/**
 * Runs the spec's optimistic patch in two passes: first it only records which cached reads it touches,
 * so those queries can be cancelled (an in-flight fetch would otherwise overwrite the patch), then it
 * applies them. Each read keeps its patches in order, so a refused call rolls back its own patch and the
 * others stay (two rows moved at once, one refused: only that one moves back).
 */
async function applyOptimistic(
  queryClient: QueryClient,
  spec: AnyActionSpec,
  input: unknown,
): Promise<Rollback> {
  if (!spec.optimistic || spec.risk !== "none") return undefined;
  const parsed = spec.input.safeParse(input);
  if (!parsed.success) return undefined;
  const patches: Patch[] = [];
  const recorder: OptimisticCache = {
    update(target, match, fn) {
      patches.push({
        spec: target,
        match: match as Record<string, unknown> | undefined,
        fn: fn as (old: unknown) => unknown,
      });
    },
  };
  spec.optimistic(parsed.data as InputOf<typeof spec>, recorder);
  if (patches.length === 0) return undefined;

  const filters = patches.map((patch) => ({
    queryKey: [ACTION_QUERY_ROOT, patch.spec.name],
    predicate: (query: { queryKey: QueryKey }) => keyMatches(query.queryKey, patch.match),
  }));
  await Promise.all(filters.map((filter) => queryClient.cancelQueries(filter)));

  const id = nextPatchId++;
  const pending = pendingOf(queryClient);
  const touched: Array<{ hash: string; key: QueryKey }> = [];
  patches.forEach((patch, index) => {
    const filter = filters[index];
    if (!filter) return;
    for (const [key, old] of queryClient.getQueriesData(filter)) {
      if (old === undefined) continue;
      const hash = JSON.stringify(key);
      let entry = pending.get(hash);
      if (!entry) {
        entry = { base: old, patches: [] };
        pending.set(hash, entry);
      }
      entry.patches.push({ id, fn: patch.fn });
      touched.push({ hash, key });
      queryClient.setQueryData(key, patch.fn(old));
    }
  });

  const settle = (agreed: boolean) => {
    for (const { hash, key } of touched) {
      const entry = pending.get(hash);
      if (!entry) continue;
      const mine = entry.patches.filter((p) => p.id === id);
      entry.patches = entry.patches.filter((p) => p.id !== id);
      if (agreed) {
        // The server agreed: this patch is now part of what the others build on.
        for (const p of mine) entry.base = p.fn(entry.base);
      } else {
        // Refused: the read again as it was, with every other call's patch still on it.
        queryClient.setQueryData(
          key,
          entry.patches.reduce((value, p) => p.fn(value), entry.base),
        );
      }
      if (entry.patches.length === 0) pending.delete(hash);
    }
  };
  return { undo: () => settle(false), keep: () => settle(true) };
}

/** After a refusal there is no server answer; refetch what the action would have touched, to be safe. */
function touchedBy(spec: AnyActionSpec, input: unknown): ObjectRef[] {
  const parsed = spec.input.safeParse(input);
  if (!parsed.success || !spec.touches) return [];
  try {
    return spec.touches({ input: parsed.data, output: undefined });
  } catch {
    return [];
  }
}

function keyMatches(key: QueryKey, match: Record<string, unknown> | undefined): boolean {
  if (!match) return true;
  const input = key[2];
  if (input === null || typeof input !== "object") return false;
  return Object.entries(match).every(([field, value]) => (input as Record<string, unknown>)[field] === value);
}

/** Refetch every active action query whose refs intersect what changed. The live channel calls this too. */
export async function invalidateRefs(queryClient: QueryClient, changed: readonly ObjectRef[]): Promise<void> {
  if (changed.length === 0) return;
  await queryClient.invalidateQueries({
    queryKey: [ACTION_QUERY_ROOT],
    predicate: (query) => {
      const refs = (query.meta as { refs?: ObjectRef[] } | undefined)?.refs;
      return refs ? refsIntersect(refs, changed) : false;
    },
  });
}

/** Refetch everything the tab shows: after the live channel reconnects (HANDOFF §6). */
export async function refetchAll(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [ACTION_QUERY_ROOT] });
}
